import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

const faqs = [
  { q: 'Làm thế nào để nạp tiền vào tài khoản?', a: 'Bạn vào mục Ngân hàng, liên kết tài khoản ngân hàng rồi tạo yêu cầu nạp tiền. Hệ thống sẽ xử lý và cộng tiền vào số dư của bạn.' },
  { q: 'Rút tiền mất bao lâu?', a: 'Yêu cầu rút tiền thường được xử lý trong vòng 1-2 giờ làm việc, tối đa 24 giờ tùy ngân hàng.' },
  { q: 'Phí giao dịch được tính như thế nào?', a: 'Chúng tôi áp dụng phí giao dịch minh bạch, không có phí ẩn. Xem chi tiết trong mục Tiện ích hoặc liên hệ hỗ trợ.' },
  { q: 'Tôi cần gì để xác minh danh tính?', a: 'Bạn cần ảnh chân dung và ảnh CMND/CCCD hai mặt. Quá trình xét duyệt thường trong vòng 24 giờ.' },
  { q: 'Giá vàng được cập nhật khi nào?', a: 'Giá vàng được cập nhật liên tục theo thời gian thực từ thị trường vàng trong nước.' },
];

export default function Support() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [requestCode, setRequestCode] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('Đã gửi yêu cầu thành công!');
      form.reset();
      return;
    }
    const name = (formData.get('name') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const subject = (formData.get('subject') as string || '').trim();
    const messageText = (formData.get('message') as string || '').trim();
    setSubmitting(true);
    setFormError('');
    setRequestCode('');
    try {
      const { data: code, error } = await supabase.rpc('create_support_request', {
        p_name: name,
        p_email: email,
        p_subject: subject,
        p_message: messageText,
      });
      if (error) throw error;
      setRequestCode(code as string);
      setStatus('success');
      setMessage('Cảm ơn bạn! Chúng tôi đã nhận được yêu cầu và sẽ phản hồi sớm.');
      form.reset();
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error && err.message ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-4xl px-4 md:px-6 py-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Trung tâm hỗ trợ</h1>
            <p className="mt-2 text-foreground-600">Chúng tôi luôn sẵn sàng giúp đỡ bạn.</p>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { icon: 'ri-phone-line', label: 'Hotline', value: '1900 1234' },
              { icon: 'ri-mail-line', label: 'Email', value: 'support@goldsbvvn.net' },
              { icon: 'ri-time-line', label: 'Giờ làm việc', value: '24/7' },
            ].map((c) => (
              <div key={c.label} className="rounded-xl border border-background-200/60 bg-background-100 p-5 text-center">
                <div className="w-11 h-11 mx-auto rounded-lg bg-primary-500/15 flex items-center justify-center mb-3">
                  <i className={`${c.icon} text-primary-400 text-xl`}></i>
                </div>
                <p className="text-xs text-foreground-600">{c.label}</p>
                <p className="text-base font-semibold text-foreground-950 mt-1">{c.value}</p>
              </div>
            ))}
          </div>

          <h2 className="text-xl font-bold text-foreground-950 mb-5">Câu hỏi thường gặp</h2>
          <div className="space-y-3 mb-10">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-semibold text-foreground-950">{f.q}</span>
                  <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-background-200/60 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Gửi yêu cầu hỗ trợ</h2>
            <p className="text-sm text-foreground-600 mb-6">Điền thông tin bên dưới, chúng tôi sẽ phản hồi trong thời gian sớm nhất.</p>

            {status === 'success' && (
              <div className="mb-5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-4">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-emerald-400 text-xl"></i>
                  <p className="text-sm text-emerald-400">{message}</p>
                </div>
                {requestCode && (
                  <div className="mt-3 rounded-md bg-background-50 border border-emerald-500/20 p-3">
                    <p className="text-xs text-foreground-600 mb-2">Mã yêu cầu của bạn (lưu lại để tra cứu trạng thái):</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-lg font-bold tracking-widest text-emerald-400">{requestCode}</span>
                      <Link to="/kiem-tra-yeu-cau" className="px-3 py-1.5 rounded-md bg-emerald-500/20 text-emerald-300 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-emerald-500/30 transition-colors">
                        Kiểm tra trạng thái
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
            {status === 'error' && (
              <div className="mb-5 rounded-lg bg-rose-500/15 border border-rose-500/30 p-4 flex items-center gap-3">
                <i className="ri-error-warning-line text-rose-400 text-xl"></i>
                <p className="text-sm text-rose-400">{formError}</p>
              </div>
            )}

            <form id="support-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                placeholder="Website"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Họ và tên</label>
                  <input name="name" required placeholder="Nguyễn Văn A" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Email</label>
                  <input name="email" type="email" required placeholder="ban@example.com" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Chủ đề</label>
                <select name="subject" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500">
                  <option>Nạp / rút tiền</option>
                  <option>Giao dịch vàng</option>
                  <option>Xác minh danh tính</option>
                  <option>Vấn đề kỹ thuật</option>
                  <option>Khác</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nội dung</label>
                <textarea name="message" required maxLength={500} rows={4} placeholder="Mô tả chi tiết vấn đề của bạn (tối đa 500 ký tự)" className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors">
                {submitting ? 'Đang gửi...' : 'Gửi yêu cầu'}
              </button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}